import { minDistToOpenPolyline } from '@/modules/game-procedural/procedural';
import { roadRibbonLayers } from '@/modules/game-procedural/road-paths';
import type { GameEngine } from '@/game/engine';
import type {
  BuildingData,
  NPC,
  NPCData,
  Town,
  TownData,
  TownObjectData,
  TownTourPhasePayload,
} from '@/game/types';
import type { InitTownPayload, RoadSegmentPayload, RoadSettlementPayload } from '@/network/api';
import { getRoads, getRoadSettlements, getTownBuildings, getTownNPCs, getTownObjects, getTownLayout, getTowns } from '@/network/towns.api';
import type { TownLayoutPayload } from '@/modules/game-domain/authored-town.types';
import { authoredTownToTownData } from './authored-town-mapper';
import { displayName } from '@/i18n/displayName';
import type { Locale } from '@/i18n/localeCodes';
import { proceduralRoadsidePOIProps, proceduralTownProps } from '@/modules/game-renderer/procedural-town-layout';

function townSizeForRadius(radius: number): string {
  return radius > 40 ? 'XL' : radius > 25 ? 'M' : radius > 15 ? 'S' : 'XS';
}

function generatedTownObjects(townId: number, centerX: number, centerY: number, radius: number, size: string): TownObjectData[] {
  return proceduralTownProps(size, townId, radius).map((item, index) => ({
    id: -(townId * 100 + index + 1),
    objectType: item.objectType,
    variant: item.variant,
    worldX: centerX + item.offsetX,
    worldY: centerY + item.offsetY,
  }));
}

export function townTourPayloadToNPCData(p: TownTourPhasePayload, loc: Locale, town: Town | null): NPCData {
  const tw = town ?? undefined;
  const npcDisplayName = displayName(loc, 'npc', {name: p.npcName, nameKey: p.npcNameKey, id: p.npcId});
  return {
    id: p.npcId ?? 0,
    name: npcDisplayName,
    nameKey: p.npcNameKey,
    type: (p.npcType ?? 'merchant') as NPCData['type'],
    worldX: p.worldX ?? 0,
    worldY: p.worldY ?? 0,
    townId: p.townId,
    townLevelMin: tw?.levelMin ?? 1,
    townLevelMax: tw?.levelMax ?? 1,
  };
}

export function townToTownData(
  town: Town,
  npcs?: NPC[],
  buildings?: BuildingData[],
  objects?: TownObjectData[],
  locale: Locale = 'en',
  authoredLayout?: TownLayoutPayload,
): TownData {
  if (authoredLayout) return authoredTownToTownData(town, authoredLayout, locale);
  const npcData: NPCData[] | undefined = npcs?.map((n) => ({
    id: n.id,
    name: displayName(locale, 'npc', n),
    nameKey: n.nameKey,
    type: n.type,
    worldX: town.worldX + n.offsetX,
    worldY: town.worldY + n.offsetY,
    buildingId: n.buildingId,
    townId: town.id,
    townLevelMin: town.levelMin,
    townLevelMax: town.levelMax,
  }));
  const size = townSizeForRadius(town.radius);
  const townObjects = objects?.length
    ? objects
    : town.biome === 'road' ? [] : generatedTownObjects(town.id, town.worldX, town.worldY, town.radius, size);
  return {
    id: town.id,
    name: displayName(locale, 'town', town),
    nameKey: town.nameKey,
    centerX: town.worldX,
    centerY: town.worldY,
    radius: town.radius,
    biome: town.biome,
    levelMin: town.levelMin,
    size,
    npcs: npcData,
    buildings,
    objects: townObjects,
  };
}

export function initTownPayloadToTown(t: InitTownPayload): Town {
  const levelMin = t.levelMin ?? 1;
  return {
    id: t.id,
    name: t.name,
    nameKey: t.nameKey,
    biome: t.biome,
    worldX: t.worldX,
    worldY: t.worldY,
    radius: t.radius,
    levelMin,
    levelMax: t.levelMax ?? levelMin,
  };
}

export function initTownPayloadToTownData(t: InitTownPayload, locale: Locale = 'en'): TownData {
  const town = initTownPayloadToTown(t);
  const npcs: NPC[] | undefined = t.npcs?.map((n) => ({
    id: n.id,
    townId: t.id,
    name: n.name,
    nameKey: n.nameKey,
    type: n.type as NPC['type'],
    offsetX: n.worldX - t.worldX,
    offsetY: n.worldY - t.worldY,
    buildingId: n.buildingId,
  }));
  return townToTownData(town, npcs, undefined, undefined, locale);
}

export function applyTownsToEngine(
  engine: GameEngine,
  towns: Town[],
  locale: Locale,
  details?: {
    townNPCMap: Map<number, NPC[]>;
    townBuildingMap: Map<number, BuildingData[]>;
    townObjectMap: Map<number, TownObjectData[]>;
    townLayoutMap?: Map<number, TownLayoutPayload>;
  },
): void {
  const townDataList = towns.map((town) =>
    townToTownData(
      town,
      details?.townNPCMap.get(town.id),
      details?.townBuildingMap.get(town.id),
      details?.townObjectMap.get(town.id),
      locale,
      details?.townLayoutMap?.get(town.id),
    ),
  );
  engine.setTowns(townDataList);
  const allNPCs: NPCData[] = [];
  for (const td of townDataList) {
    if (td.npcs) allNPCs.push(...td.npcs);
  }
  engine.setNPCs(allNPCs);
}

export function roadSettlementToTownData(
  settlement: RoadSettlementPayload,
  locale: Locale = 'en',
): TownData {
  if (settlement.layout) {
    const town=authoredTownToTownData({id:settlement.id,name:settlement.name,biome:'road',
      worldX:settlement.worldX,worldY:settlement.worldY,radius:settlement.radius,levelMin:1,levelMax:99},settlement.layout,locale);
    return {...town,name:displayName(locale, 'poi', settlement),
      spineX:settlement.spineX,spineY:settlement.spineY,entryX:settlement.entryX,entryY:settlement.entryY,
      tangentX:settlement.tangentX,tangentY:settlement.tangentY,connectorWaypoints:settlement.connectorWaypoints,
      awayX:settlement.awayX,awayY:settlement.awayY};
  }
  const isRoadsideTavern = settlement.eventId.startsWith('event.poi.tavern') || settlement.eventId.startsWith('event.poi.inn_rest');
  const savedBuildings: BuildingData[] = settlement.buildings?.map((b: NonNullable<RoadSettlementPayload['buildings']>[number]) => ({
    id: b.id,
    buildingType: b.buildingType,
    variant: b.variant ?? 0,
    worldX: b.worldX,
    worldY: b.worldY,
    facing: (b.facing ?? 'south') as BuildingData['facing'],
    footprintW: b.footprintW ?? 2,
    footprintH: b.footprintH ?? 2,
  })) ?? [];
  const awayX = settlement.awayX ?? (settlement.worldX - (settlement.spineX ?? settlement.worldX - 1));
  const awayY = settlement.awayY ?? (settlement.worldY - (settlement.spineY ?? settlement.worldY));
  const awayLength = Math.hypot(awayX, awayY) || 1;
  const fallbackBuildingX = settlement.worldX + (awayX / awayLength) * 0.5;
  const fallbackBuildingY = settlement.worldY + (awayY / awayLength) * 0.5;
  const buildings: BuildingData[] = isRoadsideTavern && savedBuildings.length === 0
    ? [{
      id: -(1_000_000_000 + settlement.id),
      buildingType: settlement.eventId.startsWith('event.poi.inn_rest') ? 'house.inn' : 'house.tavern',
      variant: settlement.id % 5,
      worldX: fallbackBuildingX,
      worldY: fallbackBuildingY,
      facing: 'south',
      footprintW: 2.5,
      footprintH: 2,
    }]
    : savedBuildings;
  const anchor = buildings[0];
  const roadsideProps = isRoadsideTavern && anchor
    ? proceduralRoadsidePOIProps(
      settlement.id,
      settlement.radius,
      anchor.worldX - settlement.worldX,
      anchor.worldY - settlement.worldY,
      awayX,
      awayY,
      settlement.tangentX ?? -awayY,
      settlement.tangentY ?? awayX,
    ).flatMap((item, index): TownObjectData[] => {
      const path=settlement.connectorWaypoints;
      const clearance=roadRibbonLayers('poi-trail')[0]![0]+.02+Math.hypot(item.footprintW,item.footprintH)/2;
      if(path&&path.length>=2&&minDistToOpenPolyline(settlement.worldX+item.offsetX,settlement.worldY+item.offsetY,path)<clearance)return [];
      return [{
      id: -(1_000_100_000 + settlement.id * 100 + index + 1),
      objectType: item.objectType,
      variant: item.variant,
      worldX: settlement.worldX + item.offsetX,
      worldY: settlement.worldY + item.offsetY,
    }];
    })
    : [];
  const npcs: NPCData[] | undefined = settlement.npcs?.map((n: NonNullable<RoadSettlementPayload['npcs']>[number]) => ({
    id: n.id,
    name: displayName(locale, 'npc', n),
    nameKey: n.nameKey,
    type: n.type as NPCData['type'],
    worldX: n.worldX,
    worldY: n.worldY,
    townId: settlement.id,
    townLevelMin: 1,
    townLevelMax: 99,
  }));
  return {
    id: settlement.id,
    name: displayName(locale, 'poi', settlement),
    centerX: settlement.worldX,
    centerY: settlement.worldY,
    radius: settlement.radius,
    biome: 'road',
    levelMin: 1,
    size: 'XS',
    spineX: settlement.spineX,
    spineY: settlement.spineY,
    entryX: settlement.entryX,
    entryY: settlement.entryY,
    tangentX: settlement.tangentX,
    tangentY: settlement.tangentY,
    connectorWaypoints: settlement.connectorWaypoints,
    awayX: settlement.awayX,
    awayY: settlement.awayY,
    buildings,
    objects: roadsideProps,
    npcs,
  };
}

export function applyRoadSettlementsToEngine(
  engine: GameEngine,
  settlements: RoadSettlementPayload[],
  locale: Locale,
  baseTowns: TownData[],
): void {
  const roadTowns = settlements.map((s) => roadSettlementToTownData(s, locale));
  engine.setTowns([...baseTowns, ...roadTowns]);
  const roadNPCs = roadTowns.flatMap((t) => t.npcs ?? []);
  const baseNPCs = baseTowns.flatMap((t) => t.npcs ?? []);
  engine.setNPCs([...baseNPCs, ...roadNPCs]);
}

/** Overlay the active visit layout (trigger-anchored) so buildings render beside the hero. */
export function applyActiveRoadPOISettlement(
  engine: GameEngine,
  active: RoadSettlementPayload,
  locale: Locale,
  roadSettlements: RoadSettlementPayload[],
  baseTowns: TownData[],
): void {
  const merged =
    roadSettlements.length > 0
      ? roadSettlements.map((s) => (s.id === active.id ? active : s))
      : [active];
  applyRoadSettlementsToEngine(engine, merged, locale, baseTowns);
}

export function restoreRoadSettlementsOnEngine(
  engine: GameEngine,
  locale: Locale,
  roadSettlements: RoadSettlementPayload[],
  baseTowns: TownData[],
): void {
  if (roadSettlements.length === 0) {
    engine.setTowns(baseTowns);
    engine.setNPCs(baseTowns.flatMap((t) => t.npcs ?? []));
    return;
  }
  applyRoadSettlementsToEngine(engine, roadSettlements, locale, baseTowns);
}

export function roadPayloadsToNetwork(roads: RoadSegmentPayload[]): Array<Array<{ x: number; y: number }>> {
  return roads.filter((r) => r.waypoints.length >= 2).map((r) => r.waypoints);
}

export function applyRoadNetworkToEngine(
  engine: GameEngine,
  roads: RoadSegmentPayload[],
): Array<Array<{ x: number; y: number }>> {
  const network = roadPayloadsToNetwork(roads);
  engine.setRoadNetwork(network);
  return network;
}

export async function fetchTownLayoutDetails(towns: Town[]) {
  const townNPCMap = new Map<number, NPC[]>();
  const townBuildingMap = new Map<number, BuildingData[]>();
  const townObjectMap = new Map<number, TownObjectData[]>();
  const townLayoutMap = new Map<number, TownLayoutPayload>();
  try {
    const [npcResults, buildingResults, objectResults, layoutResults] = await Promise.all([
      Promise.allSettled(towns.map((town) => getTownNPCs(town.id).then((npcs) => ({ townId: town.id, npcs })))),
      Promise.allSettled(towns.map((town) => getTownBuildings(town.id).then((b) => ({ townId: town.id, buildings: b })))),
      Promise.allSettled(towns.map((town) => getTownObjects(town.id).then((o) => ({ townId: town.id, objects: o })))),
      Promise.allSettled(towns.map((town) => Promise.resolve().then(() => getTownLayout(town.id)).then(layout => ({townId:town.id,layout})))),
    ]);
    for (const result of npcResults) {
      if (result.status === 'fulfilled') townNPCMap.set(result.value.townId, result.value.npcs);
    }
    for (const result of buildingResults) {
      if (result.status === 'fulfilled') townBuildingMap.set(result.value.townId, result.value.buildings);
    }
    for (const result of objectResults) {
      if (result.status === 'fulfilled') townObjectMap.set(result.value.townId, result.value.objects);
    }
    for (const result of layoutResults) {
      if (result.status === 'fulfilled' && result.value.layout) townLayoutMap.set(result.value.townId,result.value.layout);
    }
  } catch {
    /* ignore */
  }
  return { townNPCMap, townBuildingMap, townObjectMap, townLayoutMap };
}

export async function hydrateTownsFromApi(
  engine: GameEngine,
  setTownsState: (towns: Town[]) => void,
  townsRef: { current: Town[] },
  setRoadNetworkState: (network: Array<Array<{ x: number; y: number }>>, roads?: RoadSegmentPayload[]) => void,
  locale: Locale,
  isActive: () => boolean = () => true,
): Promise<{ mergedTowns: TownData[]; roadSettlements: RoadSettlementPayload[] } | void> {
  const [t, roads, roadSettlements] = await Promise.all([getTowns(), getRoads(), getRoadSettlements()]);
  if (!isActive()) return;
  setTownsState(t);
  townsRef.current = t;
  applyTownsToEngine(engine, t, locale);
  setRoadNetworkState(applyRoadNetworkToEngine(engine, roads), roads);
  const details = await fetchTownLayoutDetails(t);
  if (!isActive()) return;
  applyTownsToEngine(engine, t, locale, details);
  const mergedTowns = t.map((town) =>
    townToTownData(
      town,
      details.townNPCMap.get(town.id),
      details.townBuildingMap.get(town.id),
      details.townObjectMap.get(town.id),
      locale,
      details.townLayoutMap.get(town.id),
    ),
  );
  applyRoadSettlementsToEngine(engine, roadSettlements, locale, mergedTowns);
  return { mergedTowns, roadSettlements };
}

export function applyInitTownsToEngine(
  engine: GameEngine,
  initTowns: InitTownPayload[],
  setTownsState: (towns: Town[]) => void,
  townsRef: { current: Town[] },
  locale: Locale,
): void {
  if (initTowns.length === 0) return;
  const towns = initTowns.map(initTownPayloadToTown);
  setTownsState(towns);
  townsRef.current = towns;
  engine.setTowns(initTowns.map((t) => initTownPayloadToTownData(t, locale)));
  const allNPCs: NPCData[] = [];
  for (const td of initTowns) {
    const data = initTownPayloadToTownData(td, locale);
    if (data.npcs) allNPCs.push(...data.npcs);
  }
  engine.setNPCs(allNPCs);
}

export function applyInitRoadSettlementsToEngine(
  engine: GameEngine,
  initRoadSettlements: RoadSettlementPayload[] | undefined,
  locale: Locale,
  baseTowns: TownData[],
): void {
  if (!initRoadSettlements?.length) return;
  applyRoadSettlementsToEngine(engine, initRoadSettlements, locale, baseTowns);
}

export function applyInitRoadsToEngine(
  engine: GameEngine,
  initRoads: RoadSegmentPayload[] | undefined,
  setRoadNetworkState: (network: Array<Array<{ x: number; y: number }>>, roads?: RoadSegmentPayload[]) => void,
): void {
  if (!initRoads?.length) return;
  setRoadNetworkState(applyRoadNetworkToEngine(engine, initRoads), initRoads);
}
